import type { AccessLevel } from "@khazzin/api-client/permissions";
import type { Translate } from "@khazzin/i18n";
import { Lock, type LucideIcon, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../primitives/Button";
import { SegmentedControl } from "../primitives/SegmentedControl";
import { cx } from "../styles";
import { StatusChip } from "./StatusChip";

/**
 * A section-access grid: one row per module, each at none, read or write
 * (EP05-S04, `Employees.dc.html` "Section access", DA-05-02).
 *
 * In `ui/` because it is drawn in three places that must agree — an
 * employee's grid in Employees, the same grid in the Add Employee drawer
 * (EP05-S05), and a role's default grid read-only in Settings (EP05-S06) —
 * and three copies of "which cell is custom" would be three answers.
 *
 * **It decides nothing.** Which rows are locked, whether the grid is editable
 * at all, what the defaults are: all props. The server is the gate; this is
 * the picture of it.
 *
 * **Custom versus default.** With `roleDefaults`, a cell still at its role's
 * level says so in a quiet "role default" marker, and a cell that differs gets
 * a dot and the word "custom" for a screen reader — the dot alone is colour,
 * and colour alone is not information. Rows at none are tinted, so the
 * sections somebody cannot reach are visible at a glance without reading
 * thirteen controls.
 */

export interface SectionGridRow {
	section: string;
	label: string;
	icon?: LucideIcon;
	/** Employees, Finance, Developer: a danger chip, and usually a lock for non-owners. */
	sensitive?: boolean;
}

export interface SectionAccessGridProps {
	rows: readonly SectionGridRow[];
	value: Readonly<Record<string, AccessLevel>>;
	/** The role's levels; enables the markers and the reset link. */
	roleDefaults?: Readonly<Record<string, AccessLevel>>;
	/** Absent makes the whole grid read-only. */
	onChange?: (section: string, level: AccessLevel) => void;
	/** Sections drawn disabled with a lock, whatever `onChange` says. */
	locked?: readonly string[];
	/** Shown only when editable and at least one cell is custom. */
	onReset?: () => void;
	/** The reset link's label; defaults to the role wording. */
	resetLabel?: string;
	/**
	 * The editor's own levels (EP01-S14's ceiling rule). An option above the
	 * editor's level for its section is disabled and says why.
	 */
	ceiling?: Readonly<Record<string, AccessLevel>>;
	/** A section the server refused, drawn in the danger tone. */
	invalid?: string;
	/** Extra content after a section's label — a badge, a hint. */
	notes?: Readonly<Record<string, ReactNode>>;
	t: Translate;
	/** Names the grid for assistive technology. */
	label: string;
	className?: string;
}

const LEVELS: readonly AccessLevel[] = ["none", "read", "write"];

export function SectionAccessGrid({
	rows,
	value,
	roleDefaults,
	onChange,
	locked = [],
	onReset,
	resetLabel,
	ceiling,
	invalid,
	notes,
	t,
	label,
	className,
}: SectionAccessGridProps) {
	const editable = onChange !== undefined;
	const custom = rows.filter(
		(row) =>
			roleDefaults !== undefined &&
			levelOf(value, row.section) !== levelOf(roleDefaults, row.section),
	);

	return (
		<div className={cx("flex flex-col gap-2", className)}>
			<ul
				aria-label={label}
				className="flex flex-col divide-y divide-border rounded-sm border border-border"
			>
				{rows.map((row) => {
					const level = levelOf(value, row.section);
					const isLocked = locked.includes(row.section);
					const disabled = !editable || isLocked;
					const isCustom =
						roleDefaults !== undefined && level !== levelOf(roleDefaults, row.section);
					const Icon = row.icon;
					return (
						<li
							key={row.section}
							data-section={row.section}
							data-level={level}
							aria-invalid={invalid === row.section ? true : undefined}
							className={cx(
								"flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2",
								level === "none" && "bg-bg-subtle",
								invalid === row.section && "bg-danger-bg ring-1 ring-danger-border ring-inset",
							)}
						>
							<span className="flex min-w-0 flex-1 items-center gap-2">
								{Icon !== undefined && (
									<Icon aria-hidden size={16} className="shrink-0 text-fg-muted" />
								)}
								<span id={`section-${row.section}`} className="font-semibold text-fg">
									{row.label}
								</span>
								{row.sensitive === true && (
									<StatusChip tone="danger" size="sm">
										{t("ui.sectionGrid.sensitive")}
									</StatusChip>
								)}
								{notes?.[row.section]}
								{isLocked && (
									<Lock
										aria-label={t("ui.sectionGrid.locked")}
										role="img"
										size={14}
										className="shrink-0 text-fg-muted"
									/>
								)}
							</span>
							<span className="flex items-center gap-2">
								{roleDefaults !== undefined &&
									(isCustom ? (
										<span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-brand-ink">
											<span aria-hidden className="size-2 rounded-full bg-brand" />
											{t("ui.sectionGrid.custom")}
										</span>
									) : (
										<span className="text-[11.5px] text-fg-subtle">
											{t("ui.sectionGrid.roleDefault")}
										</span>
									))}
								<SegmentedControl<AccessLevel>
									aria-labelledby={`section-${row.section}`}
									value={level}
									options={LEVELS.map((option) => {
										const above =
											ceiling !== undefined &&
											LEVELS.indexOf(option) > LEVELS.indexOf(levelOf(ceiling, row.section));
										return {
											value: option,
											label: t(`ui.sectionGrid.level.${option}`),
											disabled: disabled || above,
											...(above ? { title: t("ui.sectionGrid.aboveOwnLevel") } : {}),
										};
									})}
									onChange={(next) => {
										if (!disabled && onChange !== undefined) {
											onChange(row.section, next);
										}
									}}
								/>
							</span>
						</li>
					);
				})}
			</ul>
			{editable && onReset !== undefined && custom.length > 0 && (
				<div>
					<Button variant="ghost" size="sm" icon={RotateCcw} onClick={onReset}>
						{resetLabel ?? t("ui.sectionGrid.reset")}
					</Button>
				</div>
			)}
		</div>
	);
}

/** A grid's level for a section; a missing entry is none, as on the server. */
function levelOf(grid: Readonly<Record<string, AccessLevel>>, section: string): AccessLevel {
	const level = grid[section];
	return level === "read" || level === "write" ? level : "none";
}
